// DTO ⇄ view model for lib/api/lookupsApi.js. build… = to server, process… = to screen.
import { formatDateInput } from '@/lib/format/date';

// `page` is 0-based server-side.
export const buildLookupParams = ({ limit, certificateType } = {}) => {
  return {
    page: 0,
    limit,
    filters: certificateType ? JSON.stringify({ certificate_type: certificateType }) : undefined,
  };
};

// `unit` is the declaration unit, `display_unit` the working one — see contracts/CLAUDE.md.
export const processMaterial = (material) => ({
  id: String(material.id),
  name: material.name ?? '',
  declarationUnit: material.unit ?? '',
  workingUnit: material.display_unit ?? '',
  // 0 means never configured.
  density: material.converted_weight_quantity || null,
  // The display_unit row the outgoing-shipment/order payloads key their quantity to.
  displayUnitId: material.display_unit_id ?? null,
  // Narrows the material dropdown to the selected site.
  siteIds: (material.site_ids ?? []).map(String),
  // Null on a material with no certificate type — belongs to every module.
  certificateType: material.certificate_type ?? null,
  // CERTIFIED | CONVENTIONAL — the entity forms offer certified ones only.
  materialType: material.material_type ?? null,
});

export const processMaterials = (dto) => (dto.output?.materials ?? []).map(processMaterial);

// GET /sites/materials/:siteId?terminalId= → the material ids assigned there, matching processMaterial's ids.
export const processSiteMaterialIds = (dto) =>
  new Set((dto?.output?.materials ?? []).map((material) => String(material.material_id)));

export const processSupplier = (supplier) => ({
  id: String(supplier.id),
  name: supplier.supplier_name ?? '',
});

export const processSuppliers = (dto) => (dto.output?.suppliers ?? []).map(processSupplier);

// "Sold to" — the id is the customer's own account, which is what a shipment stores.
export const processCustomer = (customer) => ({
  id: String(customer.customer_account_id),
  name: customer.company_name ?? '',
  code: customer.customer_id ?? '',
});

export const processCustomers = (dto) => (dto.output?.customers ?? []).map(processCustomer);

// "Shipping to (site)" — one customer's delivery sites.
export const processShippingDetail = (detail) => ({
  id: String(detail.id),
  // Manage Customers writes its "Shipping to" input to `shipping_from`; `shipping_to` stays null.
  name: detail.shipping_from || detail.shipping_to || '',
  code: detail.shipping_id ?? '',
});

export const processShippingDetails = (dto) =>
  (dto.output?.shipping_details ?? []).map(processShippingDetail);

// A directory hit is valid by construction — only its validity WINDOW still has to be checked.
export const processIsccCertificate = (dto) => {
  const cert = dto?.output?.data ?? null;
  if (!cert?.Certificate_ID) return null;
  return {
    certificateId: cert.Certificate_ID,
    holder: cert.Certificate_Holder ?? '',
    // Both go straight into a date input, and Valid_Until is blank on some directory rows.
    validFrom: formatDateInput(cert.Valid_From),
    validTo: formatDateInput(cert.Valid_Until),
  };
};

// One supplier's transport routes — the Calculate emissions modal's two cascading dropdowns.
export const processTransportationRoutes = (dto) =>
  (dto?.output?.routes ?? dto?.routes ?? []).map((route) => ({
    id: String(route.id),
    name: route.route_name ?? '',
    shippingFromId: route.shipping_from_id == null ? '' : String(route.shipping_from_id),
    shippingFromName: route.shipping_from?.shipping_from ?? route.shipping_from?.shipping_to ?? '',
    sentToSiteId: route.sent_to_site_id == null ? '' : String(route.sent_to_site_id),
    // The route carries the figure; the modal reads it rather than deriving one.
    totalTransportationEmissions: route.total_transportation_emissions ?? null,
  }));

// A site's mass-balance periods as picker options; the sites slice reads the same route into a form shape.
export const processMbPeriodOptions = (dto) =>
  (dto?.timePeriods ?? []).map((timePeriod) => ({
    value: String(timePeriod.mbPeriodId),
    label: timePeriod.name ?? '',
    startDate: timePeriod.startDate ?? null,
    endDate: timePeriod.endDate ?? null,
  }));
